import { useRef, useState, useMemo, useEffect, type ChangeEvent } from 'react'
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Tabs,
  TabList,
  Tab,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Tooltip,
  Spinner,
} from '@chakra-ui/react'
import { History, Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAccessCheck } from '@/hooks/useAccess'
import { escapeCSV, exportToCSV, importFromCSV, downloadCSVTemplate } from '@/lib/utils/csvUtils'
import { userActivityService, type AllUsersLoginHistoryRow } from '@/lib/services/userActivityService'
import { apiClient } from '@/lib/api/client'
import { useThemeColors } from '@/hooks/useThemeColors'

type TabValue = 'all' | 'trash'

export default function UsersPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [selectedRole, setSelectedRole] = useState('')
  const [roles, setRoles] = useState<any[]>([])
  const [isImporting, setIsImporting] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [isExportingLoginHistory, setIsExportingLoginHistory] = useState(false)
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [modalAction, setModalAction] = useState<'bulk' | 'role'>('bulk')
  const [modalMessage, setModalMessage] = useState('')

  const access = useAccessCheck()

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await apiClient.get('/options/roles')
        setRoles(Array.isArray(response.data) ? response.data : [])
      } catch (error: any) {
        showError(error)
      }
    }
    fetchRoles()
  }, [showError])

  const roleFilterOptions = useMemo(
    () =>
      roles.map((role: any) => ({
        value: role.name || role.id,
        label: role.name || `Role ${role.id}`,
      })),
    [roles]
  )

  const statusFilterOptions = useMemo(
    () => [
      { value: '1', label: 'Active' },
      { value: '0', label: 'Inactive' },
      { value: '2', label: 'Suspended' },
    ],
    []
  )

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        user_login: {
          name: 'Username',
          withSort: true,
          maxWidth: '150px',
        },
        user_email: {
          name: 'Email',
          withSort: true,
          maxWidth: '250px',
        },
        role_name: {
          name: 'Role',
          withSort: true,
          withFilter: true,
          filterOptions: roleFilterOptions,
          maxWidth: '150px',
        },
        approver_name: {
          name: 'Approver',
          withSort: false,
          maxWidth: '180px',
        },
        user_status: {
          name: 'Status',
          withSort: true,
          withFilter: true,
          filterOptions: statusFilterOptions,
          badge: {
            '1': 'green',
            '0': 'yellow',
            '2': 'red',
          },
          badgeLabels: {
            '1': 'Active',
            '0': 'Inactive',
            '2': 'Suspended',
          },
        },
        updated_at: {
          name: 'Updated At',
          withSort: true,
          maxWidth: '150px',
        },
      },
      dataSource: activeTab === 'trash' ? '/user-management/archived/users' : '/user-management/users',
      primaryKey: 'id',
      softDelete: true,
      displayInModal: false,
      noRecordText: 'No users found',
      otherActions:
        activeTab === 'all'
          ? [
              {
                name: 'View Activity',
                link: '/user-management/user-activity',
              },
            ]
          : undefined,
    }),
    [activeTab, roleFilterOptions, statusFilterOptions]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access]
  )

  const showSubSubCallback = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all)
      setTotalTrash(trashed)
    },
    []
  )

  const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery])

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    if (searchRef.current) {
      searchRef.current.value = ''
    }
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    const query = searchRef.current?.value || ''
    setSearchQuery(query)
    tableRef.current?.applyFilter('search', query)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleBulkActionClick = () => {
    if (!bulkAction) {
      showWarning('Please select an action first')
      return
    }
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) {
      showWarning('Please select at least one item')
      return
    }

    setModalAction('bulk')
    if (bulkAction === 'delete' && activeTab === 'trash') {
      setModalMessage('You are about to permanently delete these items from your site. This action cannot be undone.')
    } else if (bulkAction === 'reset_password') {
      setModalMessage('Password reset emails will be sent to selected users. Continue?')
    } else {
      setModalMessage('Are you sure to apply this change?')
    }
    onOpen()
  }

  const handleChangeRoleClick = () => {
    if (!selectedRole) {
      showWarning('Please select a role first')
      return
    }
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) {
      showWarning('Please select at least one item')
      return
    }
    setModalAction('role')
    setModalMessage('Are you sure you want to change the role for selected users?')
    onOpen()
  }

  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) return

    setIsBulkProcessing(true)
    try {
      let url = ''
      let payload: any = { ids: selectedIds }
      let successMessage = 'Action completed successfully'

      if (bulkAction === 'restore') {
        url = '/user-management/users/bulk/restore'
        successMessage = 'Users restored successfully'
      } else if (bulkAction === 'delete') {
        url =
          activeTab === 'trash'
            ? '/user-management/users/bulk/force-delete'
            : '/user-management/users/bulk/delete'
        successMessage =
          activeTab === 'trash' ? 'Users permanently deleted successfully' : 'Users moved to trash successfully'
      } else if (bulkAction === 'reset_password') {
        url = '/user-management/users/bulk/password'
        successMessage = 'Password reset emails sent successfully'
      }

      await apiClient.post(url, payload)
      showSuccess('Success', successMessage)
      tableRef.current?.reload()
      setBulkAction('')
      onClose()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  const handleConfirmRoleChange = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0 || !selectedRole) return

    setIsBulkProcessing(true)
    try {
      await apiClient.post('/user-management/users/bulk/role', {
        ids: selectedIds,
        role: selectedRole,
      })
      showSuccess('Success', 'User role has been changed')
      tableRef.current?.reload()
      setSelectedRole('')
      onClose()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  const handleImport = () => {
    fileInputRef.current?.click()
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      if (event.target) {
        event.target.value = ''
      }
      return
    }

    setIsImporting(true)
    try {
      await importFromCSV({
        endpoint: '/user-management/users/import',
        file,
        showError,
        showSuccess,
        showWarning,
        onSuccess: () => {
          tableRef.current?.reload()
        },
      })
    } finally {
      setIsImporting(false)
      if (event.target) {
        event.target.value = ''
      }
    }
  }

  const handleDownloadTemplate = () => {
    downloadCSVTemplate({
      csvHeaders: [
        'Username',
        'Email',
        'Password',
        'First Name',
        'Last Name',
        'Employee ID',
        'Position',
        'Role',
        'Status',
      ],
      exampleRow: [
        'johndoe',
        'john.doe@example.com',
        'TempPass123!',
        'John',
        'Doe',
        'EMP001',
        'Software Developer',
        'Employee',
        '1',
      ],
      comments: [
        'Note: Status values: 1 = Active, 0 = Inactive, 2 = Suspended',
        'Role should match an existing role name in the system (example: Employee, Admin)',
      ],
      filename: 'users_template.csv',
      showError,
      showSuccess,
    })
  }

  const formatLoginHistoryDateTime = (value: string | undefined | null) => {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const loginHistoryDeviceLabel = (entry: AllUsersLoginHistoryRow) => {
    const di = entry.device_info
    if (!di || (!di.device && !di.browser)) return ''
    return `${di.device} - ${di.browser} (${di.os})`
  }

  const handleExportLoginHistory = async () => {
    setIsExportingLoginHistory(true)
    try {
      const { data } = await userActivityService.exportAllUsersLoginHistory({ limit: 50000 })
      if (!data?.length) {
        showWarning('Export', 'No login history to export')
        return
      }

      const csvHeaders = [
        'User ID',
        'Username',
        'Email',
        'Date & Time',
        'Type',
        'Status',
        'IP Address',
        'Device',
        'Location',
        'Reason',
        'Last Activity',
        'Expires At',
      ]

      const csvRows = data.map((entry) => [
        entry.user_id,
        entry.user_login || '',
        entry.user_email || '',
        formatLoginHistoryDateTime(entry.timestamp),
        entry.type || '',
        entry.status || '',
        entry.ip_address || '',
        loginHistoryDeviceLabel(entry),
        entry.location || '',
        entry.reason ?? '',
        formatLoginHistoryDateTime(entry.last_activity),
        formatLoginHistoryDateTime(entry.expires_at),
      ])

      const csvContent = [
        csvHeaders.map(escapeCSV).join(','),
        ...csvRows.map((row) => row.map(escapeCSV).join(',')),
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      const filename = `users_login_history_${new Date().toISOString().split('T')[0]}.csv`
      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      showSuccess('Export Complete', `Successfully exported ${data.length} login history row(s)`)
    } catch (error: any) {
      showError(error)
    } finally {
      setIsExportingLoginHistory(false)
    }
  }

  const handleExportData = async () => {
    setIsExporting(true)
    try {
      await exportToCSV({
        endpoint: '/user-management/users',
        selectedIds: [],
        activeTab,
        searchQuery: '',
        csvHeaders: [
          'ID',
          'Username',
          'Email',
          'First Name',
          'Last Name',
          'Employee ID',
          'Position',
          'Role',
          'Role ID',
          'Status',
          'Status Code',
          'Created At',
          'Updated At',
          'Deleted At',
        ],
        dataMapper: (user: any) => [
          user.id || '',
          user.user_login || '',
          user.user_email || '',
          user.first_name || user.user_details?.first_name || '',
          user.last_name || user.user_details?.last_name || '',
          user.employee_id || user.user_details?.employee_id || '',
          user.position || user.user_details?.position || '',
          user.role_name || 'Unassigned',
          user.role_id || '',
          user.user_status === 1 ? 'Active' : user.user_status === 0 ? 'Inactive' : 'Suspended',
          user.user_status || '',
          user.created_at || '',
          user.updated_at || '',
          user.deleted_at || '',
        ],
        filename: `users_export_all${activeTab === 'trash' ? '_trash' : ''}_${new Date().toISOString().split('T')[0]}.csv`,
        showError,
        showSuccess,
        showWarning,
      })
    } catch (error: any) {
      showError(error)
    } finally {
      setIsExporting(false)
    }
  }

  const handleConfirm = () => {
    if (modalAction === 'bulk') {
      handleConfirmBulkAction()
    } else {
      handleConfirmRoleChange()
    }
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                Users
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                Manage user accounts and their details
              </Text>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange}>
                    <TabList borderBottom="none">
                      <Tab>
                        All{' '}
                        <Badge ml={2} colorScheme="blue">
                          {totalRows}
                        </Badge>
                      </Tab>
                      {totalTrash > 0 && (
                        <Tab>
                          Trash{' '}
                          <Badge ml={2} colorScheme="red">
                            {totalTrash}
                          </Badge>
                        </Tab>
                      )}
                    </TabList>
                  </Tabs>
                  <Box pb={2}>
                    <HStack spacing={2}>
                      {activeTab === 'all' && access.can_export && (
                        <Tooltip label="Export login history (CSV)" placement="top">
                          <IconButton
                            aria-label="Export login history"
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            icon={
                              isExportingLoginHistory ? <Spinner size="sm" /> : <History size={16} />
                            }
                            onClick={handleExportLoginHistory}
                            isDisabled={isExportingLoginHistory || isExporting}
                          />
                        </Tooltip>
                      )}
                      <ActionButtons
                        pathname="/user-management/users"
                        onExport={handleExportData}
                        onImport={handleImport}
                        onDownloadTemplate={handleDownloadTemplate}
                        createLabel="Create New User"
                        isExporting={isExporting}
                        isImporting={isImporting}
                        fileInputRef={fileInputRef}
                        onFileUpload={handleFileUpload}
                        acceptFileTypes=".csv,.xlsx,.xls"
                      />
                    </HStack>
                  </Box>
                </HStack>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack spacing={4} flexWrap="wrap">
                {access.can_delete && (
                  <HStack spacing={2} minW="250px">
                    <Select
                      placeholder="Bulk actions"
                      size="sm"
                      value={bulkAction}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                        e.stopPropagation()
                        setBulkAction(e.target.value)
                      }}
                    >
                      {activeTab === 'trash' && <option value="restore">Restore</option>}
                      {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                      {activeTab === 'all' && <option value="delete">Delete</option>}
                      {activeTab === 'all' && <option value="reset_password">Send password reset</option>}
                    </Select>
                    <Button size="sm" colorScheme="blue" onClick={handleBulkActionClick}>
                      Apply
                    </Button>
                  </HStack>
                )}

                {activeTab === 'all' && access.can_edit && (
                  <HStack spacing={2} minW="250px">
                    <Select
                      placeholder="Change role to..."
                      size="sm"
                      value={selectedRole}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                        e.stopPropagation()
                        setSelectedRole(e.target.value)
                      }}
                    >
                      {roles.map((role: any) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </Select>
                    <Button size="sm" colorScheme="blue" onClick={handleChangeRoleClick}>
                      Change
                    </Button>
                  </HStack>
                )}

                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search by name, email..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody
              p={0}
              pt={1}
              pb={4}
              mx={5}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
              position="relative"
            >
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  params={tableParams}
                  access={permissions}
                  setSubSub={showSubSubCallback}
                  onOpenModal={(id) => navigate(`/user-management/users/${id}`)}
                  filterOptions={{
                    role_name: roleFilterOptions,
                    user_status: statusFilterOptions,
                  }}
                />
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmation</ModalHeader>
          <ModalBody>
            <Text>{modalMessage}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isBulkProcessing}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleConfirm} isLoading={isBulkProcessing} loadingText="Processing...">
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}


